import { Injectable } from '@angular/core';
import { CoverageLegend } from './models/coverage-legend.model';
import { ApiCoverageDetail, ApiQuoteResponse, ApiBenefit } from '@bhtp-client-api/base';
import { Constants } from '../shared/settings/constants';

@Injectable()
export class CoverageService {
    constructor() {
    }

    /**
     *   Gets a list of icons to be used for the coverage limit legend, based on packages passed in.
     *
     *   Returns an empty array if none are found.
     */
    public getCoverageLegendIconsForProducts(objectsWithCoverageGroups: ApiQuoteResponse[], selectedOnly: boolean = false): CoverageLegend[] {
        let legendInfoToReturn: CoverageLegend[] = [];

        let coverageLimitPerTypes: string[] = [];
        objectsWithCoverageGroups.forEach((o) => {
            if (o && o.coverages) {
                let coverages: ApiCoverageDetail[] = [];

                if (o.coverages.included) {
                    coverages = coverages.concat(o.coverages.included);
                }
                if (o.coverages.optional) {
                    let optional = o.coverages.optional;
                    if (selectedOnly) {
                        optional = o.coverages.optional.filter(f => {
                            return f.isSelected;
                        });
                    }
                    coverages = coverages.concat(optional);
                }
                if (o.coverages.bonus) {
                    coverages = coverages.concat(o.coverages.bonus);
                }

                // get the unique cover LimitPer values in the selected packages
                coverages.forEach((c) => {
                    if (c.limitPer && coverageLimitPerTypes.indexOf(c.limitPer) < 0) {
                        coverageLimitPerTypes.push(c.limitPer);
                    }
                });
            }
        });

        // build the coverage legends based on the unique coverage limitPer values
        coverageLimitPerTypes.forEach((limitPerType) => {
            let coverageLegend: CoverageLegend = this.getCoverageLegendForLimitPerType(limitPerType);
            if (coverageLegend) {
                legendInfoToReturn.push(coverageLegend);
            }
        });

        return legendInfoToReturn;
    }

    public getCoverageIcon(coverage: ApiCoverageDetail): string {
        return this.getCoverageLegendForLimitPerType(coverage.limitPer).icon;
    }

    public isCoverageSelectedWithAllData(coverage: ApiCoverageDetail): boolean {
        if (coverage.isSelected) {
            if (this.isCarRentalCoverage(coverage)) {
                if (coverage.daySpan && coverage.daySpan > 0) {
                    return true;
                }
            }
            else if (this.hasMultipleLimits(coverage)) {
                if (coverage.coverageLimit && coverage.coverageLimit > 0) {
                    return true;
                }
            }
            else {
                return true;
            }
        }

        return false;
    }

    public getOptionalCoveragePremium(coverage: ApiCoverageDetail): number {
        if (this.isCoverageSelectedWithAllData(coverage)) {
            if (this.hasPremiumsByDay(coverage) && coverage.daySpan) {
                return Number(coverage.premiumsByDay[coverage.daySpan]);
            }
            else if (this.hasPremiumsByLimit(coverage) && coverage.coverageLimit) {
                return Number(coverage.premiumsByLimit[coverage.coverageLimit]);
            }
            else {
                return Number(coverage.premium);
            }
        }

        return 0;
    }

    public hasPremiumsByLimit(coverage: ApiCoverageDetail): boolean {
        if (coverage.premiumsByLimit) {
            for (let key in coverage.premiumsByLimit) {
                // if there is at least one key, then return true
                return true;
            }
        }

        return false;
    }

    public hasPremiumsByDay(coverage: ApiCoverageDetail): boolean {
        if (coverage.premiumsByDay) {
            for (let key in coverage.premiumsByDay) {
                // if there is at least one key, then return true
                return true;
            }
        }

        return false;
    }
    
    public hasMultipleLimits(coverage: ApiCoverageDetail): boolean {
        return coverage && Constants.MULTIPLE_LIMIT_RATING_IDS.indexOf(coverage.ratingId) > -1 && coverage.limits && coverage.limits.length > 1;
    }
    
    public isCarRentalCoverage(coverage: ApiCoverageDetail): boolean {
        return coverage && Constants.CAR_RENTAL_RATING_IDS.indexOf(coverage.ratingId) > -1;
    }

    /**
     *  Maps the original smart quote response to pull bonus coverages (included coverages with type of 'Extra')
     *      out of included coverages property and into a bonus coverages property.
     *      Prevents having to parse elsewhere.
     *
     *
     * @param products the original
     */
    public remapBonusCoverages(products: ApiQuoteResponse[]): ApiQuoteResponse[] {
        products.forEach((p) => {
            let bonusCoverages: ApiCoverageDetail[] = [];

            if (!p || !p.coverages || !p.coverages.included) {
                // nothing to do if we don't have a product or included coverages.
                return;
            }

            // find all bonus coverages
            p.coverages.included.forEach((c) => {
                if (c && c.type === 'Extra') {
                    bonusCoverages.push(c);
                }
            });

            // remove any found bonus coverages from the included coverages list.
            //  this is a separate loop so we don't modify the array being iterated over.
            bonusCoverages.forEach((bc) => {
                p.coverages.included.splice(p.coverages.included.indexOf(bc), 1);
            });

            try {
                p.coverages.bonus = bonusCoverages.length ? bonusCoverages : undefined;
            } catch(e) {

            }
        });

        
        return products;
    }

    private getCoverageLegendForLimitPerType(limitPerType: string): CoverageLegend {
        let coverageLegend: CoverageLegend = new CoverageLegend();
        coverageLegend.limitPer = limitPerType;

        switch (limitPerType) {
            case 'Trip':
                coverageLegend.icon = 'fa-suitcase';
                coverageLegend.message = 'Coverages are per policy trip cost.';
                break;
            case 'Person':
                coverageLegend.icon = 'fa-user';
                coverageLegend.message = 'Coverages are per traveler.';
                break;
            case 'Family':
                coverageLegend.icon = 'fa-users';
                coverageLegend.message = 'Coverages are per family.';
                break;
            case 'Vehicle':
                coverageLegend.icon = 'fa-car';
                coverageLegend.message = 'Coverages are per vehicle.';
                break;
            case 'Flight':
                coverageLegend.icon = 'fa-plane';
                coverageLegend.message = 'Coverages are per flight.';
                break;
            case 'Connection':
                coverageLegend.icon = 'fa-exchange';
                coverageLegend.message = 'Coverages are per Connection.';
                break;
        }

        return coverageLegend;
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/coverage-modal/coverage.service.ts